import { Button, Divider, Form, Input, message, Popover, Radio, Select, Typography, Row, Col } from 'antd'
import { QuestionCircleOutlined, CloseOutlined, CheckOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import {
  CLUSTERING_BASED_ON_ATTR_AND_REL_KEYS,
  CLASSIFICATION_BASED_ON_ATTR_AND_REL_VALUES,
  REGRESSION_BASED_ON_ATT_AND_REL_VALUES,
  CLASSIFICATION_BASED_ON_ATTR_AND_REL_KEYS,
  CLUSTERING_BASED_ON_ATTR_AND_REL_VALUES,
  parseFeatures,
  parseTarget,
  trainingTypes,
  train
} from './helpers'
import { DownOutlined } from '@ant-design/icons'
import './index.scss'
{ Option } = Select
{ Paragraph, Text } = Typography

TrainML = ({ service: { name } }) ->

  [form] = Form.useForm()

  [showFeatures, setShowFeatures] = useState(false)
  [showTarget, setShowTarget] = useState(false)
  [featuresValid, setFeaturesValid] = useState(false)
  [showErrFields, setShowErrFields] = useState(false)

  onFinish = ({
    featureAtts,
    queryBody,
    relationsIn,
    relationsOut,
    targetKey,
    targetType,
    trainingId,
    trainingType
  }) ->
    if showFeatures
      featureParams = parseFeatures(featureAtts, relationsIn, relationsOut)
    if showTarget
      targetParams = parseTarget(targetKey, targetType)
    try
      train({ queryBody, trainingId, trainingType, featureParams, targetParams, name })
      message.success("Training submitted. You can close this window and check the status of training using the Training Status service.");
    catch e
      message.error("An error occured: #{e.message}")

  validateFeatureFields = ->
    {
      featureAtts,
      relationsIn,
      relationsOut
    } = form.getFieldsValue(['featureAtts', 'relationsIn', 'relationsOut'])
    if featureAtts or relationsIn or relationsOut
      form.setFields([{
        name: 'features',
        errors: []
      }])
    else
      form.setFields([{
        name: 'features',
        errors: ['A field is required'],
      }])

  selectTrainingType = (val) ->
    if val is CLUSTERING_BASED_ON_ATTR_AND_REL_KEYS then setShowFeatures(false)
    else if val is CLASSIFICATION_BASED_ON_ATTR_AND_REL_KEYS then setShowFeatures(false)
    else setShowFeatures(true)

    if val is CLUSTERING_BASED_ON_ATTR_AND_REL_VALUES then setShowTarget(false)
    else if val is CLUSTERING_BASED_ON_ATTR_AND_REL_KEYS then setShowTarget(false)
    else setShowTarget(true)

  featureFields = [
    {
      name: "featureAtts"
      placeholder: "Attributes"
    },
    {
      name: "relationsOut"
      placeholder: "Relations Out"
    },
    {
      name: "relationsIn"
      placeholder: "Relations In"
    }
  ]

  featureParamsText = (
    <div style={{ width: 500, lineHeight: '1.6rem' }}>
      <Text type="secondary">
        Guidelines:
        <ul>
          <li>
            At least one field is required (Attributes, Relations In, Relations Out)
          </li>
          <li>
            Attribute or relation should identically match that of the relation or attribute in the data
          </li>
          <li>
            Multiple attributes or relations should be separated by a ',' without whitespace inbetween
          </li>
          <li>
            Asterisk wildcard (*) can be used to select all relations or attributes
          </li>
          <li>
            Example:
            <ul>
              <li>isType, *, hasName, isRelation <CloseOutlined style={{color: '#e44'}}/></li>
              <li>isType,hasName,isRelation <CheckOutlined style={{color: '#4e4'}}/></li>
            </ul>
          </li>
        </ul>
      </Text>
    </div>
  )

  trainingIdText = (
    <div style={{ width: 500, lineHeight: '1.6rem' }}>
      <Text type="primary">
        Choose your own training id which can be any keyword like <span style={fontStyle: 'italic'}>my first training</span>.
        The training will gets saved in Weaver using this ID so that you can check the status of training using the Training Status service.
      </Text>
    </div>
  )

  trainingDatasetText = (
    <div style={{ width: 500, lineHeight: '1.6rem' }}>
      <Text type="primary">
        Weaver needs a training dataset to train the model on.
        This dataset should exclude data you would like to use the model for prediction later on.
        Use a Weaver Query to define the set of Elements targeted for training. You can use the Graph Console to test out the query first.
      </Text>
    </div>
  )

  # Add later
  # ..., while clustering is a unsupervised learning approach.
  # <li>
  #   Clustering is the task of partitioning the dataset into groups, called clusters.
  #   The goal is to split up the data in such a way that points within single cluster are very similar
  #   and points in different clusters are different. It determines grouping among unlabeled data.
  #   It predicts continuous valued output.
  # </li>

  trainingTypeText = (
    <div style={{ width: 500, lineHeight: '1.6rem' }}>
      <Text type="primary">
        Select which Learning Approach to employ. Classification and Regression are supervised learning approach that maps an input to an output
        based on example input-output pairs. You can choose between the options described below.
      </Text>
      <div style={{marginBottom: 20}}></div>
      <Text type="secondary">
        <ul>
          <li>
            <div style={{fontWeight: 'bold'}}>Classification on keys</div>
            In classification the data is categorized under different labels according to some parameters
            and then the labels are predicted for the data. Classifying emails as either spam or not spam
            is example of classification problem. Choose this option if you would like to classify on the possible keys an Element has.
            It would for example classify elements on their types as persons or cars.
          </li>
          <li>
            <div style={{fontWeight: 'bold', marginTop: 10}}>Classification on values</div>
            Choose this option if you would like to classify on the possible values an Element has.
            It would for example classify persons on their age value.
          </li>
          <li>
            <div style={{fontWeight: 'bold', marginTop: 10}}>Regression on values</div>
            The Regression analysis is the statistical model which is used to predict the numeric data instead of labels.
            It can also identify the distribution trends based on the available data or historic data.
            Predicting a person’s income from their age, education is example of regression task.
            It predicts discrete number of values.
          </li>
        </ul>
      </Text>
    </div>
  )

  featureFormItems = (
    <Form.Item
      className="feature-form"
      name='features'
      onChange={ -> validateFeatureFields()}
      style={{textAlign: 'left'}}
    >
      <span style={{padding: '15px 15px 15px 0px'}}>Feature Parameters</span>
      <Popover
        content={featureParamsText}
        title='Feature Paramaters'
        placement="right"
        trigger="click"
      >
        <a><QuestionCircleOutlined style={{paddingBottom: '15px'}}/></a>
      </Popover>
      { featureFields.map((field) ->
        <Form.Item
          { ...field  }
          key={"train-form-#{field.name}"}
        >
          <Input
            type="text"
            placeholder={field.placeholder}
            key={"train-form-#{field.name}-input"}
          />
        </Form.Item>
      )}
    </Form.Item>
  )

  targetFormItems =(
    <>
      <Divider />
      <Row className='target-form'>
        <Col span={6}>
          <Form.Item name="targetType">
            <Select>
              <Option value="attribute" key="attribute">
                attribute
              </Option>
              <Option value="relationOut" key="relationOut">
                relationOut
              </Option>
              <Option value="relationIn" key="relationIn">
                relationIn
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={3}/>
        <Col span={15}>
          <Form.Item
            name="targetKey"
            rules={[{ required: true, message: 'Target key required' }]}
          >
            <Input
              placeholder="Target key"
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  )

  <div className="ml-form-container">
    <Form
      form={form}
      onFinish={onFinish}
      className="ml-action-form"
      initialValues={{
        trainingType: trainingTypes[0].type,
        targetType: 'attribute'
      }}
    >
      <div style={{textAlign: 'left'}}>
        <span style={{padding: '15px 15px 15px 0px'}}>Training ID</span>
        <Popover
            content={trainingIdText}
            title='Training ID'
            placement="right"
            trigger="click"
          >
          <a><QuestionCircleOutlined style={{paddingBottom: '15px'}}/></a>
        </Popover>
      </div>
      <Form.Item
        name="trainingId"
        rules={[{ required: true, message: 'The Training ID is required, click the help button for more info.' }]}
        style={{marginBottom: '15px'}}
      >
        <Input placeholder="Choose a Training ID (required)"/>
      </Form.Item>
      <div style={{textAlign: 'left'}}>
        <span style={{padding: '15px 15px 15px 0px'}}>Learning Approach</span>
        <Popover
            content={trainingTypeText}
            title='Learning Approach'
            placement="right"
            trigger="click"
          >
          <a><QuestionCircleOutlined style={{paddingBottom: '15px'}}/></a>
        </Popover>
      </div>
      <Form.Item name="trainingType" style={{marginBottom: 15}}>
        <Select
          showSearch
          placeholder="Training Type"
          optionFilterProp="children"
          filterOption={(input, option) ->
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          onChange={selectTrainingType}
        >
          {
            trainingTypes.map(({ name, type }) ->
              <Option
                value={type}
                key={type}
              >
                { name }
              </Option>
            )
          }
        </Select>
      </Form.Item>
      <div style={{textAlign: 'left'}}>
        <span style={{padding: '15px 15px 15px 0px'}}>Training Dataset</span>
        <Popover
            content={trainingDatasetText}
            title='Training Dataset'
            placement="right"
            trigger="click"
          >
          <a><QuestionCircleOutlined style={{paddingBottom: '15px'}}/></a>
        </Popover>
      </div>
      <Form.Item
        name="queryBody"
        style={{marginBottom: '0px'}}
      >
        <Input.TextArea
          placeholder="Provide a Weaver Query defining the dataset on which the training will apply. If left empty, it will use the entire Space dataset."
          rows={4}
          autosize={{maxRows: 1}}
          type="text"
        />
      </Form.Item>
      { if showFeatures then featureFormItems }
      { if showTarget then targetFormItems }
      <Form.Item
        name="trainButton"
        style={{ textAlign: 'center', marginTop: '20px' }}
      >
        <Button htmlType="submit">
          Start Training
        </Button>
      </Form.Item>
    </Form>
  </div>

export default TrainML
